import { z } from 'zod';

// Server-side environment variables schema (sensitive data)
const serverSchema = z.object({
  // Database
  DATABASE_URL: z.string().url(),

  // Auth
  BETTER_AUTH_SECRET: z.string().min(1),
  ACCOUNT_CREATION_CODE: z.string().min(1),

  // NocoDB API Configuration (SERVER-ONLY - SENSITIVE)
  NOCODB_API_URL: z.string().url(),
  NOCODB_API_TOKEN: z.string().min(1),

  // NocoDB Table IDs (SERVER-ONLY - SENSITIVE)
  NOCODB_ARTICLES_TABLE_ID: z.string().min(1),
  NOCODB_SECTIONS_TABLE_ID: z.string().min(1),
  NOCODB_IMAGE_STACKS_TABLE_ID: z.string().min(1),
  NOCODB_IMAGES_TABLE_ID: z.string().min(1),
  NOCODB_STANDARD_ARTICLE_IMAGES: z.string().min(1),
  NOCODB_ERRORS_TABLE_ID: z.string().min(1),
  NOCODB_SOCIAL_MEDIA_TABLE_ID: z.string().min(1),
  NOCODB_ARTICLE_IDEAS_TABLE_ID: z.string().min(1),

  // Categories upstream (SERVER-ONLY)
  CATEGORIES_API_BASE_URL: z.string().url().optional(),

  // Webhook Authentication (SERVER-ONLY - SENSITIVE)
  // WEBHOOK_API_KEY removed - not used in codebase

  // n8n Webhook URLs (SERVER-ONLY - SENSITIVE)
  N8N_HOOK_WORKFLOW_1_START: z.string().url(),
  N8N_HOOK_WORKFLOW_2_GENERATE_IMAGES: z.string().url(),
  N8N_HOOK_WORKFLOW_3_REGENERATE_IMAGES: z.string().url(),
  N8N_HOOK_WORKFLOW_4_GENERATE_ARTICLE: z.string().url(),
  N8N_HOOK_WORKFLOW_5_GENERATE_SOCIAL_MEDIA: z.string().url(),
  N8N_HOOK_WORKFLOW_6_SOCIAL_MEDIA_REVIEW: z.string().url(),
  N8N_HOOK_WORKFLOW_7_MASTER_SCHEDULER: z.string().url(),
  N8N_HOOK_WORKFLOW_8_REVIEW_PROMPTS: z.string().url(),
  N8N_HOOK_WORKFLOW_8_SPECIALISED_POSTS: z.string().url(),
  N8N_HOOK_WORKFLOW_9_STANDARD_ARTICLE_GENERATION_START: z.string().url(),
  N8N_HOOK_WORKFLOW_10_STANDARD_IMAGE_GENERATION: z.string().url(),
  N8N_HOOK_WORKFLOW_11_STANDARD_IMAGE_REGENERATION: z.string().url(),
  N8N_HOOK_WORKFLOW_12_STANDARD_ARTICLE_GENERATION: z.string().url(),

  // n8n Webhook Authentication Headers (SERVER-ONLY - SENSITIVE)
  N8N_WEBHOOK_AUTH_HEADER_NAME: z.string().min(1),
  N8N_WEBHOOK_AUTH_HEADER_VALUE: z.string().min(1),

  // Bunny CDN Configuration (SERVER-ONLY - SENSITIVE)
  BUNNY_STORAGE_ZONE: z.string().min(1),
  BUNNY_STORAGE_ACCESS_KEY: z.string().min(1),
  BUNNY_PULL_ZONE: z.string().min(1),
  BUNNY_API_KEY: z.string().min(1).optional(),
  REPLICATE_API_TOKEN: z.string().min(1).optional(),
  INITIAL_IMAGE_GENERATION_BACKEND: z.enum(['n8n', 'native']).default('n8n'),
  CODEX_LB_BASE_URL: z.string().url().optional(),
  CODEX_LB_API_KEY: z.string().min(1).optional(),
  INITIAL_IMAGE_PROMPT_MODEL: z.string().min(1).optional(),
  INITIAL_IMAGE_SLUG_MODEL: z.string().min(1).optional(),
  INITIAL_IMAGE_MODEL: z.string().min(1).optional(),
  INITIAL_IMAGE_PORTRAIT_SIZE: z.string().regex(/^\d+x\d+$/).optional(),
  INITIAL_IMAGE_LANDSCAPE_SIZE: z.string().regex(/^\d+x\d+$/).optional(),
  INITIAL_IMAGE_SIZE_MAPPING: z.enum(['normal', 'transposed']).optional(),
  CANVA_EXTENSION_API_KEY: z.string().min(1),
  CANVA_EXTENSION_SYNC_PREFIX: z.string().min(1).optional(),
  CANVA_EXTENSION_RELEASE_PREFIX: z.string().min(1).optional(),

  // Optional server vars
  NODE_ENV: z
    .enum(['development', 'test', 'production'])
    .default('development'),
  API_PORT: z.coerce.number().default(3000),
});

// Client-side environment variables schema (public data)
const clientSchema = z
  .object({
    // Public website URLs (safe to expose)
    VITE_WEBSITE_URL: z.string().url(),

    // Internal API routes (safe - these are our own secure proxy routes)
    VITE_API_BASE_URL: z.string().url().optional(),
  })
  .transform((clientEnv) => ({
    ...clientEnv,
    VITE_API_BASE_URL:
      clientEnv.VITE_API_BASE_URL ?? clientEnv.VITE_WEBSITE_URL,
  }));

// Determine if we're running on server or client
const isServer = typeof window === 'undefined';

// Create environment object with proper validation
function createEnv() {
  if (isServer) {
    // Server-side: validate all environment variables
    // Skip validation if explicitly requested
    if (process.env.SKIP_ENV_VALIDATION === 'true') {
      return {
        ...process.env,
        // Ensure client vars are available
        VITE_WEBSITE_URL: process.env.VITE_WEBSITE_URL,
        VITE_API_BASE_URL: process.env.VITE_API_BASE_URL,
      } as Partial<ServerEnv & ClientEnv>;
    }

    const serverEnv = serverSchema.parse(process.env);
    const clientEnv = clientSchema.parse(process.env);

    return {
      ...serverEnv,
      ...clientEnv,
    };
  }

  // Client-side: only validate and expose VITE_ prefixed variables
  // No access to process.env on client, use import.meta.env instead
  const clientEnv = clientSchema.parse({
    VITE_WEBSITE_URL: import.meta.env.VITE_WEBSITE_URL,
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  });

  return clientEnv;
}

export const env = createEnv();

// Type definitions for better TypeScript support
export type ServerEnv = z.infer<typeof serverSchema>;
export type ClientEnv = z.infer<typeof clientSchema>;
export type Env = ServerEnv & ClientEnv;
